/**
 * AI Code Sanitizer — Generic reliability layer
 *
 * Cleans ALL LLM code generation issues before preview.
 * GENERIC: No hardcoded package lists. Receives installed packages dynamically.
 *
 * Fixes:
 * - Duplicate React imports → "Identifier 'React' has already been declared"
 * - Merged components in one file → split/keep first only
 * - Markdown artifacts (```jsx etc.) accidentally in source
 * - Broken defaultProps / propTypes → removed entirely
 * - Stray commas, unclosed brackets → patched
 * - Multiple default exports → keep first
 * - TypeScript syntax in .js files → stripped
 * - Mixed module systems (require + import) → normalized
 * - Missing React/hooks/router imports → auto-injected
 * - Adjacent JSX elements → wrapped in fragment
 * - Broken Route syntax → fixed
 */

export function sanitizeFile(code, filePath = '', installedPackages = []) {
  if (!code || typeof code !== 'string') return code;

  let result = code;

  // Order matters — split merged files first, then clean each
  result = splitMergedComponents(result);
  result = removeMarkdownArtifacts(result);
  result = fixBrokenDefaultProps(result);
  result = removeDefaultProps(result);
  result = removePropTypes(result);
  result = stripTypeScript(result);
  result = fixDuplicateImports(result);
  result = fixDuplicateDeclarations(result);
  result = fixBrokenDestructuring(result);
  result = fixJsxColonAttributes(result);
  result = fixUndefinedShorthandProps(result);
  result = fixStrayCommasAndBrackets(result);
  result = fixBrokenRouteSyntax(result);
  result = fixAdjacentJSXElements(result);
  result = fixMissingReactImport(result);
  result = fixMissingHooksImport(result);
  result = fixMissingRouterImport(result);
  result = fixMixedModuleSystems(result);
  result = stubUnknownPackages(result, installedPackages);
  result = fixMultipleDefaultExports(result);
  result = cleanupWhitespace(result);

  return result;
}

export function sanitizeAllFiles(spFiles, installedPackages = []) {
  const result = { ...spFiles };
  for (const fp of Object.keys(result)) {
    if (fp.endsWith('.js') || fp.endsWith('.jsx') || fp.endsWith('.ts') || fp.endsWith('.tsx')) {
      result[fp] = sanitizeFile(result[fp], fp, installedPackages);
    }
  }
  return result;
}

// ─── SANITIZERS ──────────────────────────────────────────────────────────────

function removeDefaultProps(code) {
  return code.replace(/\w+\.defaultProps\s*=\s*\{[^}]*\};?/gs, '');
}

/**
 * Fix broken defaultProps that produce stray commas/syntax errors.
 * Pattern: ComponentName.defaultProps = { \n  key: value,\n}; followed by garbage
 * or defaultProps with trailing comma after last property causing parse error.
 */
function fixBrokenDefaultProps(code) {
  // Remove entire defaultProps blocks including malformed ones
  // This catches cases where the block is malformed (stray comma at start, etc.)
  code = code.replace(/\w+\.defaultProps\s*=\s*\{[\s\S]*?\};?\s*$/gm, (match) => {
    // If the match looks broken (starts with comma, has empty lines), remove it
    const inner = match.replace(/\w+\.defaultProps\s*=\s*\{/, '').replace(/\};?\s*$/, '');
    if (inner.trim().startsWith(',') || inner.trim() === '') {
      return '';
    }
    return match;
  });

  // Remove lines that are just a stray comma (common artifact of broken defaultProps)
  code = code.replace(/^\s*,\s*$/gm, '');

  return code;
}

function removePropTypes(code) {
  return code
    .replace(/\w+\.propTypes\s*=\s*\{[\s\S]*?\};?/gs, '')
    .replace(/import\s+PropTypes\s+from\s+['"]prop-types['"];?\s*/g, '');
}

function stripTypeScript(code) {
  return code
    // Strip a TS primitive type annotation (`const x: number =`, `(p: string)`,
    // `foo: boolean;`) — but ONLY when it's genuinely an annotation, i.e. an
    // `identifier : type` pair. TERNARY-COLON-FIX: this regex used to have no
    // left anchor at all, so a plain-JS conditional expression whose tail is a
    // bare keyword — `promo ? promo.code : null;`, `x ? y : undefined,` — had
    // its `: null` / `: undefined` stripped, producing `promo ? promo.code ;`,
    // an unrecoverable parse error. Confirmed live: this broke CartContext.js
    // (a `: null` ternary in placeOrder), so `CartProvider` failed to export,
    // `<CartProvider>` rendered `undefined`, and every generated app that uses
    // it went to a white screen ("Element type is invalid ... got: undefined").
    // `null`/`undefined` are NOT in the list: they're never TS annotations in
    // generated JS, but `key: null,` (an object value) would be rewritten to
    // `key,` (shorthand of an undefined variable) — corrupting valid code.
    // Now: require an identifier immediately before the colon, and skip the
    // match when a `?` appears earlier in the same statement (the ternary
    // shape) — MULTI-LINE-TERNARY-FIX: looking back only to the start of the
    // current line missed a `?` written on an earlier line of the same
    // ternary (`cond\n  ? a\n  : null;`), which would still have gotten
    // corrupted the same way as the single-line case above. Look back to the
    // nearest statement boundary (`;` `{` `}`) instead of just the last
    // newline, so a `?` anywhere in the same statement is still seen.
    .replace(
      /([A-Za-z_$][\w$]*)\s*:\s*(?:string|number|boolean|any|void|never)(?:\[\])?\s*([,;)=\n])/g,
      (match, ident, tail, offset, str) => {
        const stmtStart = Math.max(str.lastIndexOf(';', offset - 1), str.lastIndexOf('{', offset - 1), str.lastIndexOf('}', offset - 1)) + 1;
        if (str.slice(stmtStart, offset).includes('?')) return match; // ternary, not an annotation
        return ident + tail;
      }
    )
    .replace(/:\s*React\.FC(\<[^>]*\>)?/g, '')
    // Strip TypeScript 'as' type assertions but NOT import aliases (import { X as Y })
    // Only strip 'as Type' when preceded by ) or ] or a value expression, not inside import {}
    .replace(/([)\]\w"'`])\s+as\s+(string|number|boolean|any|unknown|never|const)(?![\w])/g, '$1')
    .replace(/interface\s+\w+\s*\{[^}]*\}/gs, '')
    .replace(/type\s+\w+\s*=\s*[^;]+;/g, '')
    .replace(/useState<[^>]+>/g, 'useState')
    .replace(/useRef<[^>]+>/g, 'useRef')
    .replace(/useCallback<[^>]+>/g, 'useCallback')
    .replace(/useMemo<[^>]+>/g, 'useMemo')
    .replace(/Array<([^>]+)>/g, '$1[]')
    .replace(/Promise<[^>]+>/g, 'Promise')
    .replace(/Record<[^>]+>/g, 'object')
    .replace(/(Partial|Omit|Pick|Required|Readonly)<[^>]+>/g, 'object')
    .replace(/\)\s*:\s*\w+(\[\])?\s*\{/g, ') {')
    .replace(/\)\s*:\s*\w+(\[\])?\s*=>/g, ') =>');
}

function fixDuplicateImports(code) {
  const lines = code.split('\n');
  const seen = new Map(); // module path → line index
  const toRemove = new Set();

  lines.forEach((line, idx) => {
    const importMatch = line.match(/^import\s+.*from\s+['"]([^'"]+)['"]/);
    if (importMatch) {
      const modulePath = importMatch[1];
      const key = line.trim();
      if (seen.has(key)) {
        toRemove.add(idx);
      } else {
        seen.set(key, idx);
      }
    }
  });

  let result = lines.filter((_, idx) => !toRemove.has(idx)).join('\n');

  // Fix duplicate named imports within same import statement
  // e.g. import { BrowserRouter as Router, BrowserRouter } → keep BrowserRouter (direct usage wins)
  result = result.replace(
    /import\s+\{([^}]+)\}\s+from\s+['"]([^'"]+)['"]/g,
    (match, names, pkg) => {
      const items = names.split(',').map(n => n.trim()).filter(Boolean);
      const seen = new Map();
      for (const item of items) {
        const asMatch = item.match(/^(\w+)\s+as\s+(\w+)$/);
        const importedName = asMatch ? asMatch[1] : item;
        // Prefer direct import over alias
        if (!seen.has(importedName) || !asMatch) {
          seen.set(importedName, item);
        }
      }
      return `import { ${[...seen.values()].join(', ')} } from '${pkg}'`;
    }
  );

  return result;
}

function fixDuplicateDeclarations(code) {
  const importedNames = [];
  const importRegex = /^import\s+(\w+)\s+from\s+/gm;
  let m;
  while ((m = importRegex.exec(code)) !== null) {
    importedNames.push(m[1]);
  }
  for (const name of importedNames) {
    const hasDuplicate = code.match(new RegExp(`(export\\s+default\\s+function|export\\s+function|function|const)\\s+${name}\\s*[({=]`));
    if (hasDuplicate) {
      code = code.replace(new RegExp(`^import\\s+${name}\\s+from\\s+[^\\n]+\\n?`, 'm'), '');
    }
  }
  return code;
}

function fixBrokenDestructuring(code) {
  // Fix destructured children
  //
  // PARAM-VS-RETURNED-OBJECT-FIX: meant for a destructured FUNCTION PARAMETER
  // (`function Layout({ children }) {`), but the same `({ children` shape
  // also matches an arrow function's body wrapping a RETURNED/CONSTRUCTED
  // object literal (`() => ({ children, label })`) — adding `= null` there
  // is a default-value initializer inside an object literal, invalid syntax
  // ("Invalid shorthand property initializer"), the same bug class confirmed
  // live for the sibling `items`/`data`/... fix in LivePreview.js. A
  // parameter list's own `(` is never preceded by `=>`; a returned-object-
  // literal's `(` always is.
  code = code
    .replace(/\(\{\s*children\s*\}\)/g, (m, offset, full) => (/=>\s*$/.test(full.slice(Math.max(0, offset - 20), offset)) ? m : '({ children = null })'))
    .replace(/\(\{\s*children\s*,/g, (m, offset, full) => (/=>\s*$/.test(full.slice(Math.max(0, offset - 20), offset)) ? m : '({ children = null,'));
  
  // RUNTIME SAFETY: Guard .toLocaleString(), .toFixed(), .toString() on potentially undefined values
  // Pattern: {value.toLocaleString()} → {(value || 0).toLocaleString()}
  code = code.replace(
    /\{([a-zA-Z_][a-zA-Z0-9_.]*)\.toLocaleString\(\)\}/g,
    '{($1 || 0).toLocaleString()}'
  );
  code = code.replace(
    /\{([a-zA-Z_][a-zA-Z0-9_.]*)\.toFixed\(([^)]*)\)\}/g,
    '{($1 || 0).toFixed($2)}'
  );
  // Guard .length on potentially undefined
  code = code.replace(
    /\{([a-zA-Z_][a-zA-Z0-9_.]*)\.length\}/g,
    '{($1 || []).length}'
  );
  // Guard .map/.filter on nested properties: items.products.map → (items.products || []).map
  code = code.replace(
    /([a-zA-Z_]\w*(?:\.[a-zA-Z_]\w*)+)\.(map|filter|forEach|find|reduce)\(/g,
    '($1 || []).$2('
  );
  // Guard .map/.filter on simple variables: products.map( → (products || []).map(
  // But skip: React.map, Array.map, Object.keys, etc.
  code = code.replace(
    /\{([a-zA-Z_]\w*)\.(map|filter|forEach|find|reduce)\(/g,
    (match, varName, method) => {
      // Skip known safe objects
      if (['React', 'Array', 'Object', 'Math', 'JSON', 'console', 'window', 'document', 'navigator'].includes(varName)) {
        return match;
      }
      return `{(${varName} || []).${method}(`;
    }
  );
  
  return code;
}

/**
 * Fix JSX attributes written with colon syntax instead of equals.
 * LLM mistake: <input type: "text" required: '' className: "..." />
 * Should be: <input type="text" required className="..." />
 */
function fixJsxColonAttributes(code) {
  function fixAttrs(text) {
    // Fix boolean: required: '' or required: "" -> required
    text = text.replace(/(\s)(\w+):\s*(['"])\3/g, '$1$2');
    // Fix attribute: "value" -> attribute="value"
    text = text.replace(/(\s)(\w+):\s*"([^"]*)"/g, '$1$2="$3"');
    // Fix attribute: 'value' -> attribute="value"
    text = text.replace(/(\s)(\w+):\s*'([^']+)'/g, '$1$2="$3"');
    // Fix attribute: {value} -> attribute={value}
    text = text.replace(/(\s)(\w+):\s*(\{[^}]+\})/g, '$1$2=$3');
    return text;
  }

  return code.replace(/<[a-zA-Z][\w.]*[^>]*\/?>/g, (tag) => {
    // Skip tags with inline styles {{ }} — don't touch those colons
    if (tag.includes('{{')) {
      const parts = tag.split(/(\{\{[^}]*\}\})/);
      return parts.map(p => p.startsWith('{{') ? p : fixAttrs(p)).join('');
    }
    return fixAttrs(tag);
  });
}

/**
 * Fix object shorthand properties that reference undefined variables.
 * LLM mistake: { from, to, rating, status } as shorthand when those vars don't exist.
 * Detects ANY bare word inside an object context (not just a predefined list).
 */
function fixUndefinedShorthandProps(code) {
  // Words that ARE commonly defined as real variables — skip these
  const SAFE_VARS = new Set([
    'true', 'false', 'null', 'undefined', 'NaN', 'Infinity',
    'children', 'props', 'state', 'key', 'ref', 'id', 'e', 'i', 'j', 'k',
    'item', 'index', 'data', 'err', 'error', 'res', 'req', 'next',
    'navigate', 'location', 'params', 'match', 'history',
    'useState', 'useEffect', 'useRef', 'useCallback', 'useMemo',
    'React', 'Component', 'Fragment',
  ]);

  const lines = code.split('\n');
  const result = [];

  // Track defined variables (const/let/var/function/import)
  //
  // BUG FOUND LIVE: the simple `const NAME` match below never matched
  // destructuring — `const [cartItems, dispatch] = useReducer(...)` or
  // `const { cartItems } = something` — so cartItems/wishlist (declared
  // this exact way in every cart/wishlist context this session) never
  // made it into definedVars. Later, when the SAME context exposed them
  // as shorthand properties in its context value object (`{ cartItems,
  // wishlist, ... }` — the standard React Context pattern), this function
  // "fixed" them into `cartItems: ''`/`wishlist: ''`, silently replacing
  // the real reducer state with a permanent empty string. Any component
  // reading `useCart().cartItems` and calling `.map()`/`.reduce()` on it
  // then crashes at render (calling an array method on a string), taking
  // the whole app down to a blank screen — reproduced live end-to-end
  // from a real "Build an ecommerce website" generation.
  const definedVars = new Set();
  for (const line of lines) {
    const constMatch = line.match(/^\s*(?:const|let|var)\s+(\w+)/);
    if (constMatch) definedVars.add(constMatch[1]);
    // Array destructuring: const [a, b] = ... (useState/useReducer/etc.)
    const arrayDestructureMatch = line.match(/^\s*(?:const|let|var)\s*\[([^\]]+)\]/);
    if (arrayDestructureMatch) {
      arrayDestructureMatch[1].split(',').forEach(p => {
        const name = p.trim().replace(/=.*$/, '').trim();
        if (name) definedVars.add(name);
      });
    }
    // Object destructuring: const { a, b: c } = ... — the LOCAL binding
    // name is what matters (the part after ":" for renamed properties).
    const objectDestructureMatch = line.match(/^\s*(?:const|let|var)\s*\{([^}]+)\}/);
    if (objectDestructureMatch) {
      objectDestructureMatch[1].split(',').forEach(p => {
        const renamed = p.split(':')[1] || p;
        const name = renamed.trim().replace(/=.*$/, '').trim();
        if (name) definedVars.add(name);
      });
    }
    const funcMatch = line.match(/^\s*(?:function|export\s+(?:default\s+)?function)\s+(\w+)/);
    if (funcMatch) definedVars.add(funcMatch[1]);
    const paramMatch = line.match(/(?:function\s+\w+|=>)\s*\(([^)]+)\)/);
    if (paramMatch) paramMatch[1].split(',').forEach(p => definedVars.add(p.trim().replace(/[={].*/,'')));
    // ARROW-PARAM-FIX: `const fn = (name, email, password) => {` puts the param
    // list BEFORE the `=>`, so the pattern above (which anchors on `=>` first)
    // never captured it. Those params are real bindings — without this, valid
    // object shorthand built from them (`const u = { name, email, password }`,
    // the standard pattern) got rewritten to `name: ''` etc., silently blanking
    // the values.
    const arrowParamMatch = line.match(/\(([^)]*)\)\s*=>/);
    if (arrowParamMatch) arrowParamMatch[1].split(',').forEach(p => {
      const name = p.trim().replace(/[:={].*/, '').trim().replace(/^\.\.\./, '');
      if (name) definedVars.add(name);
    });
    // IMPORT-BINDING-FIX: a default/namespace import (`import api from
    // '../services/api'`) was never added to definedVars, so a bare `api`
    // used later (e.g. `api\n.get(...)`) looked "undefined" to the
    // shorthand-prop heuristic above. Covers both the default binding and
    // any named imports on the same line (`import React, { useState } from
    // 'react'`).
    const importDefaultMatch = line.match(/^\s*import\s+(\w+)\s*(?:,|from)/);
    if (importDefaultMatch) definedVars.add(importDefaultMatch[1]);
    const importNamedMatch = line.match(/^\s*import\s+(?:\w+\s*,\s*)?\{([^}]+)\}/);
    if (importNamedMatch) {
      importNamedMatch[1].split(',').forEach(p => {
        const renamed = p.split(/\s+as\s+/i).pop().trim();
        if (renamed) definedVars.add(renamed);
      });
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const stripped = line.trim();

    // Match standalone shorthand: "    word," or "    word" (indented, nothing else)
    const match = stripped.match(/^([a-z]\w*)(,?)\s*$/);
    if (match) {
      const word = match[1];
      const trailing = match[2];
      const indent = line.match(/^(\s*)/)[1];

      // Skip if it's a known safe variable or already defined
      if (SAFE_VARS.has(word) || definedVars.has(word) || indent.length < 4) {
        result.push(line);
        continue;
      }

      // Check context: are we inside an object literal?
      //
      // BUG FOUND LIVE: a standalone JSX boolean attribute (`required`,
      // `disabled`, `checked`, `autoFocus`, ...) on its own line is
      // completely valid JSX — but the very next check below treats
      // "previous non-empty line ends in a quote" as evidence of being
      // inside an object literal, and a preceding string-valued JSX
      // attribute (`className="...sm:col-span-2"`) ALSO ends in a quote.
      // That false match turned a real `<input required .../>` into
      // `<input required: '' .../>` — invalid JSX, hard Babel parse
      // failure, blank preview. A JSX tag opener (`<input`, `<div ...`,
      // a bare `/>` or `>`) is the actual nearest context for an
      // attribute line and is never itself an object literal, so it must
      // be checked FIRST and short-circuit the lookback entirely.
      let inObject = false;
      for (let j = i - 1; j >= Math.max(0, i - 6); j--) {
        const prev = result[j] ? result[j].trim() : lines[j].trim();
        if (!prev) continue;
        if (/^<[A-Za-z]/.test(prev) || prev === '/>' || prev === '>' || /^\/?>/.test(prev)) {
          break; // JSX tag context, not an object literal
        }
        // TEMPLATE-LITERAL-INTERPOLATION-FIX: a line ending in `${` opens a
        // template-literal expression slot (e.g. a className built with a
        // template string), not an object literal — even though it also
        // ends with the character `{`, which the generic check below would
        // otherwise match first. Confirmed live: `className={`...${` on one
        // line, followed by a bare ternary condition (`isActive`) alone on
        // the next, got misread as "inside an object literal", rewriting
        // that plain identifier reference into `isActive: ''` — invalid
        // inside a ternary, hard Babel parse failure, blank preview.
        if (prev.endsWith('${')) {
          break; // template-literal expression context, not an object literal
        }
        // BLOCK-BRACE-FIX: a line ending in `{` is only an object-literal
        // opener when the char right before it is one that actually starts
        // an object (`=`, `:`, `(`, `[`, `,`, or `return`) — e.g. `const x = {`,
        // `return {`, `foo({`. A function/control-flow block also ends in a
        // bare `{` (`useEffect(() => {`, `) {`, `try {`, `} else {`) but is
        // never an object literal. Confirmed live: `useEffect(() => {`
        // followed by a plain `api` (an imported identifier, not declared
        // via const/let/var) on the next line got misread as an object
        // shorthand prop and rewritten to `api: ''`, so the following
        // `.get(...)` chain call bound to the empty string instead —
        // `"".get is not a function` at runtime, with the stored source
        // completely clean (this check only runs at render time).
        if (prev.endsWith('{') && !/(?:=|:|\(|\[|,|\breturn)\s*\{$/.test(prev)) {
          break; // function/control-flow block opener, not an object literal
        }
        // JSX-ATTRIBUTE-LOOKBACK-FIX: a JSX attribute with a quoted value
        // (`type='text'`, `to="/services"`) also "ends in a quote", the
        // exact same shape as a real object-literal string property when
        // only the immediately preceding line is examined — but it's JSX-
        // attribute context, not an object literal. Confirmed live:
        // `<input type='text' required />`'s `required` line looked back
        // one line to `type='text'`, matched the broad ends-in-quote
        // fallback below, and got "fixed" into `required: ''` — invalid
        // JSX, hard Babel parse failure. A `key=value` shape (equals sign)
        // is JSX-attribute syntax; a real object property uses a colon
        // (`key: value`, already covered by the check just before this
        // one) — so the quote-ending fallback must not also fire for the
        // equals-sign shape.
        const looksLikeJsxAttr = /^[\w-]+=/.test(prev);
        if (prev.endsWith('{') || prev.endsWith(',') || /^\w+\s*:/.test(prev) || (!looksLikeJsxAttr && /['"]\s*,?$/.test(prev))) {
          inObject = true;
          break;
        }
        if (prev.endsWith(';') || prev.startsWith('import ') || prev.startsWith('const ') || prev.startsWith('return') || prev.startsWith('export')) {
          break;
        }
      }

      if (inObject) {
        result.push(`${indent}${word}: ''${trailing}`);
        continue;
      }
    }

    result.push(line);
  }

  return result.join('\n');
}

/**
 * Fix stray commas, unclosed brackets, and other syntax artifacts
 * that cause "Unexpected token" Babel errors.
 */
function fixStrayCommasAndBrackets(code) {
  // Remove lines that are ONLY a comma (artifact of broken code generation)
  code = code.replace(/^\s*,\s*$/gm, '');

  // Fix trailing comma before closing brace on same line: {foo: 'bar',}
  // This is actually valid JS, but fix double commas: {foo: 'bar',,}
  code = code.replace(/,\s*,/g, ',');

  // Fix object with leading comma: { , key: value }
  code = code.replace(/\{\s*,/g, '{');

  // Remove empty statements that are just semicolons on their own after removal
  code = code.replace(/^\s*;\s*$/gm, '');

  // Fix: style: '', followed by }; on next line (broken defaultProps artifact)
  // Pattern: a line ending with `';` or `'',` that's followed by `};` — this is fine
  // But a line that's just `  style: '',` followed by `};'` is broken
  code = code.replace(/\};\s*['"`]\s*$/gm, '};');

  return code;
}

/**
 * Fix broken React Router <Route /> syntax.
 * Common AI errors:
 * - <Route path="/" component={X} /> (v5 syntax)
 * - <Route path="/"><X /></Route> (v5 children syntax)
 * - Missing closing /> on Route
 */
function fixBrokenRouteSyntax(code) {
  // Fix v5 component prop → v6 element prop
  code = code.replace(
    /<Route\s+([^>]*?)component=\{(\w+)\}([^>]*?)\s*\/>/g,
    '<Route $1element={<$2 />}$3 />'
  );

  // Fix v5 render prop → v6 element prop
  code = code.replace(
    /<Route\s+([^>]*?)render=\{[^}]*\}([^>]*?)\s*\/>/g,
    (match, before, after) => {
      const pathMatch = (before + after).match(/path=["']([^"']+)["']/);
      const path = pathMatch ? pathMatch[1] : '/';
      return `<Route path="${path}" element={<div />} />`;
    }
  );

  // Fix <Switch> → <Routes>
  code = code.replace(/<Switch>/g, '<Routes>');
  code = code.replace(/<\/Switch>/g, '</Routes>');

  // Fix exact prop (not needed in v6)
  code = code.replace(/<Route\s+exact\s+/g, '<Route ');

  return code;
}

/** Count open/close JSX tags; ignores > inside {...} or quotes (e.g. arrow functions in props). */
function _countJsxTags(jsxBlock) {
  const tagCounts = {};
  let i = 0;
  while (i < jsxBlock.length) {
    if (jsxBlock[i] !== '<') {
      i++;
      continue;
    }

    if (jsxBlock[i + 1] === '/') {
      // Tag name must allow dotted member-expression components (e.g.
      // </CartContext.Provider>, </Context.Consumer>) — a standard React
      // pattern. The open-tag match below already accepts this shape; this
      // regex previously required the name to end right at ">"/whitespace,
      // so any dotted closing tag silently failed to match at all, was
      // never counted as a close, and got treated as permanently unclosed —
      // triggering a bogus extra `</TagName>` to be appended, which broke
      // Babel parsing and caused the whole file to be replaced with a
      // stub (wiping out its real exports). Reproduced live with a
      // CartContext.Provider component.
      const closeMatch = jsxBlock.slice(i).match(/^<\/([a-zA-Z][a-zA-Z0-9]*(?:\.[a-zA-Z][a-zA-Z0-9]*)*)\s*>/);
      if (closeMatch) {
        const tag = closeMatch[1];
        tagCounts[tag] = (tagCounts[tag] || 0) - 1;
        i += closeMatch[0].length;
        continue;
      }
      i++;
      continue;
    }

    const openMatch = jsxBlock.slice(i).match(/^<([a-zA-Z][a-zA-Z0-9]*(?:\.[a-zA-Z][a-zA-Z0-9]*)*)/);
    if (!openMatch) {
      i++;
      continue;
    }

    const tag = openMatch[1];
    let j = i + openMatch[0].length;
    let braceDepth = 0;
    let inQuote = null;

    while (j < jsxBlock.length) {
      const c = jsxBlock[j];
      if (inQuote) {
        if (c === inQuote && jsxBlock[j - 1] !== '\\') inQuote = null;
        j++;
        continue;
      }
      if (c === '"' || c === "'") {
        inQuote = c;
        j++;
        continue;
      }
      if (c === '{') {
        braceDepth++;
        j++;
        continue;
      }
      if (c === '}') {
        braceDepth = Math.max(0, braceDepth - 1);
        j++;
        continue;
      }
      if (c === '>' && braceDepth === 0) {
        const selfClose = jsxBlock[j - 1] === '/';
        if (!selfClose) tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        j++;
        break;
      }
      j++;
    }
    i = j;
  }
  return tagCounts;
}

/**
 * MARKET-STANDARD-FIX-V2: Removed destructive JSX tag balancing.
 * Adjacent JSX is now handled by auto-fix loop (LLM-based fix).
 * This function only does SAFE minimal fixes.
 */
function fixAdjacentJSXElements(code) {
  // Fix 1: Remove empty element={} attributes
  code = code.replace(/\s+element=\{\s*\}/g, '');

  // Fix 2: Fix double closing tags
  code = code.replace(/(<\/\w+>)>/g, '$1');

  // Fix 3: Remove content after "export default X" line
  const exportMatch = code.match(/^export default \w+\s*;?\s*$/m);
  if (exportMatch) {
    const exportEnd = code.indexOf(exportMatch[0]) + exportMatch[0].length;
    const afterExport = code.substring(exportEnd).trim();
    // If there's JSX garbage after export, remove it
    if (afterExport && (afterExport.startsWith('<') || afterExport.startsWith('/'))) {
      code = code.substring(0, exportEnd) + '\n';
    }
  }

  // Fix 4: Proper JSX tag balancing using stack-based parsing
  // Only operate within return() blocks
  const returnMatch = code.match(/return\s*\(/);
  if (returnMatch) {
    const returnStart = code.indexOf(returnMatch[0]);
    const parenStart = code.indexOf('(', returnStart);
    
    // Find matching closing paren
    let depth = 1;
    let i = parenStart + 1;
    while (i < code.length && depth > 0) {
      if (code[i] === '(' && code[i-1] !== "'") depth++;
      if (code[i] === ')' && code[i+1] !== "'") depth--;
      i++;
    }
    const parenEnd = i - 1;
    
    if (parenEnd > parenStart) {
      let jsxBlock = code.substring(parenStart + 1, parenEnd);
      
      const tagCounts = _countJsxTags(jsxBlock);
      
      // Add missing closing tags at the end of JSX block
      let additions = '';
      for (const [tag, count] of Object.entries(tagCounts)) {
        if (count > 0) {
          for (let j = 0; j < count; j++) {
            additions += `\n      </${tag}>`;
          }
        }
      }
      
      // Remove extra closing tags
      for (const [tag, count] of Object.entries(tagCounts)) {
        if (count < 0) {
          for (let j = 0; j < Math.abs(count); j++) {
            const closeTag = `</${tag}>`;
            const lastClose = jsxBlock.lastIndexOf(closeTag);
            if (lastClose > -1) {
              jsxBlock = jsxBlock.substring(0, lastClose) + jsxBlock.substring(lastClose + closeTag.length);
            }
          }
        }
      }
      
      if (additions) {
        jsxBlock = jsxBlock.trimEnd() + additions;
      }
      
      code = code.substring(0, parenStart + 1) + jsxBlock + code.substring(parenEnd);
    }
  }

  return code;
}

function fixMissingReactImport(code) {
  if ((code.includes('<') && code.includes('/>')) || code.includes('React.')) {
    if (!code.match(/import\s+React/)) {
      code = 'import React from "react";\n' + code;
    }
  }
  // Fix missing ReactDOM import when ReactDOM.createRoot is used
  if (code.includes('ReactDOM.') && !code.match(/import\s+ReactDOM/)) {
    code = 'import ReactDOM from "react-dom/client";\n' + code;
  }
  return code;
}

function fixMissingHooksImport(code) {
  const hooks = ['useState', 'useEffect', 'useCallback', 'useMemo', 'useRef'];
  const usedHooks = hooks.filter(h => code.includes(h + '('));
  if (usedHooks.length > 0 && !code.match(/import.*\{[^}]*(useState|useEffect)[^}]*\}.*from\s+['"]react['"]/)) {
    if (code.match(/import\s+React\s+from\s+['"]react['"]/)) {
      code = code.replace(
        /import\s+React\s+from\s+['"]react['"];?/,
        `import React, { ${usedHooks.join(', ')} } from "react";`
      );
    } else if (!code.match(/import.*from\s+['"]react['"]/)) {
      code = `import React, { ${usedHooks.join(', ')} } from "react";\n` + code;
    }
  }
  return code;
}

function fixMissingRouterImport(code) {
  // Use regex to detect Router/BrowserRouter/Routes/Route/Link usage in JSX or hooks
  const routerUsagePatterns = [
    /<\/?Router[\s>]/,
    /<\/?BrowserRouter[\s>]/,
    /<\/?HashRouter[\s>]/,
    /<\/?Link[\s>]/,
    /<\/?NavLink[\s>]/,
    /<\/?Route[\s>]/,
    /<\/?Routes[\s>]/,
    /<\/?Navigate[\s>]/,
    /<\/?Outlet[\s>]/,
    /\buseNavigate\s*\(/,
    /\buseLocation\s*\(/,
    /\buseParams\s*\(/,
    /\buseSearchParams\s*\(/,
  ];
  const needsRouter = routerUsagePatterns.some(p => p.test(code));
  const hasRouterImport = /from\s+['"]react-router-dom['"]/.test(code);
  if (needsRouter && !hasRouterImport) {
    const needed = [];
    if (/<\/?Router[\s>]/.test(code)) needed.push("BrowserRouter as Router");
    if (/<\/?BrowserRouter[\s>]/.test(code)) needed.push("BrowserRouter");
    if (/<\/?Link[\s>]/.test(code) || /<\/?NavLink[\s>]/.test(code)) needed.push("Link");
    if (/<\/?Routes[\s>]/.test(code)) needed.push("Routes");
    if (/<\/?Route[\s>]/.test(code)) needed.push("Route");
    if (/<\/?Navigate[\s>]/.test(code)) needed.push("Navigate");
    if (/<\/?Outlet[\s>]/.test(code)) needed.push("Outlet");
    if (/\buseNavigate\s*\(/.test(code)) needed.push("useNavigate");
    if (/\buseLocation\s*\(/.test(code)) needed.push("useLocation");
    if (/\buseParams\s*\(/.test(code)) needed.push("useParams");
    if (/\buseSearchParams\s*\(/.test(code)) needed.push("useSearchParams");
    if (needed.length === 0) needed.push("BrowserRouter as Router", "Routes", "Route", "Link", "useNavigate");
    code = "import { " + [...new Set(needed)].join(", ") + " } from \"react-router-dom\";\n" + code;
  }

  // SAFETY: If code uses <Router> but import only has BrowserRouter (no alias), add const Router = BrowserRouter
  if (/<\/?Router[\s>]/.test(code) && /from\s+['"]react-router-dom['"]/.test(code)) {
    const importLine = code.match(/import\s+\{([^}]+)\}\s+from\s+['"]react-router-dom['"]/);
    if (importLine) {
      const imports = importLine[1];
      // Check if Router is available (either directly imported or aliased)
      const hasRouter = /\bRouter\b/.test(imports);
      if (!hasRouter && /\bBrowserRouter\b/.test(imports)) {
        // BrowserRouter is imported but not aliased as Router — add alias line
        code = code.replace(importLine[0], importLine[0] + '\nconst Router = BrowserRouter;');
      }
    }
  }

  return code;
}

function fixMixedModuleSystems(code) {
  if (code.includes('import ') && code.includes('require(')) {
    code = code.replace(/const\s+(\w+)\s*=\s*require\(['"][^'"]+['"]\);?\s*/g, '');
    code = code.replace(/const\s+\{[^}]+\}\s*=\s*require\(['"][^'"]+['"]\);?\s*/g, '');
  }
  return code;
}

/**
 * GENERIC package stubbing — stubs ANY import not in installedPackages.
 * No hardcoded list. Works for any LLM output.
 * 
 * CRITICAL: Handles aliased imports like `import { v4 as uuidv4 }` correctly.
 * The alias (uuidv4) becomes the const name, not "v4 as uuidv4".
 */
function stubUnknownPackages(code, installedPackages) {
  // Helper: precise package match (react-icons should NOT match 'react')
  const isInstalled = (pkg) => installedPackages.some(a => pkg === a || pkg.startsWith(a + '/'));

  // Named imports: import { X, Y } from 'unknown-pkg'
  // Also handles: import { X as Y } from 'unknown-pkg'
  code = code.replace(
    /import\s+\{([^}]*)\}\s+from\s+['"]([^'"./][^'"]*)['"];?/g,
    (match, names, pkg) => {
      if (isInstalled(pkg)) return match;
      const items = names.split(',').map(n => n.trim()).filter(Boolean);
      return items.map(raw => {
        // Handle "X as Y" aliases — use the alias (Y) as the variable name
        const aliasMatch = raw.match(/^(\w+)\s+as\s+(\w+)$/);
        const name = aliasMatch ? aliasMatch[2] : raw;
        // If it looks like a utility function (not a component), stub as function
        if (name[0] === name[0].toLowerCase() || name.match(/^(v4|uuid|hash|compare|sign|verify|encode|decode)/)) {
          return `const ${name} = () => 'stub-${name}';`;
        }
        return `const ${name} = ({ children, className, ...p }) => React.createElement('div', { className, ...p }, children);`;
      }).join('\n');
    }
  );
  // Default imports: import X from 'unknown-pkg'
  code = code.replace(
    /import\s+(\w+)\s+from\s+['"]([^'"./][^'"]*)['"];?/g,
    (match, name, pkg) => {
      if (isInstalled(pkg)) return match;
      return `const ${name} = () => null;`;
    }
  );
  return code;
}

function cleanupWhitespace(code) {
  return code.replace(/\n{4,}/g, '\n\n\n');
}


// ─── CRITICAL: Split merged multi-component files ────────────────────────────

/**
 * When AI generates multiple components in one file (separated by comments like
 * "// src/components/X.jsx" or markdown blocks), keep only the FIRST component.
 * This prevents: "Identifier 'React' has already been declared" and
 * "Only one default export allowed per module" errors.
 */
function splitMergedComponents(code) {
  const lines = code.split('\n');

  for (let i = 5; i < lines.length; i++) {
    const line = lines[i].trim();

    // Pattern 1: "// src/components/X.jsx" or "// src/pages/X.jsx"
    if ((line.startsWith('// src/') || line.startsWith('// components/') ||
         line.startsWith('// pages/') || line.startsWith('// ./')) &&
        (line.endsWith('.jsx') || line.endsWith('.js') || line.endsWith('.tsx') || line.endsWith('.ts'))) {
      return lines.slice(0, i).join('\n').trim();
    }

    // Pattern 2: Second "import React" (clear sign of merged files)
    if (line.startsWith('import React') && i > 3) {
      const above = lines.slice(0, i).join('\n');
      if (above.includes('import React')) {
        const cutAt = lines[i - 1].trim() === '' ? i - 1 : i;
        return lines.slice(0, cutAt).join('\n').trim();
      }
    }

    // Pattern 3: Second "export default" without being inside a function
    if (line.startsWith('export default ') && i > 10) {
      const above = lines.slice(0, i).join('\n');
      if (above.includes('export default ')) {
        return lines.slice(0, i).join('\n').trim();
      }
    }

    // Pattern 4: "---" separator followed by another component (AI sometimes uses this)
    if (line === '---' && i > 10 && i < lines.length - 5) {
      const nextNonEmpty = lines.slice(i + 1).find(l => l.trim() !== '');
      if (nextNonEmpty && (nextNonEmpty.trim().startsWith('import ') || nextNonEmpty.trim().startsWith('//'))) {
        return lines.slice(0, i).join('\n').trim();
      }
    }
  }

  return code;
}

/**
 * Remove markdown code blocks and headings that AI sometimes includes in code output.
 */
function removeMarkdownArtifacts(code) {
  return code
    .replace(/```(jsx|js|javascript|tsx|typescript|python|css|html)?(\s*\n)?/g, '')
    .replace(/```(\s*\n)?/g, '')
    .replace(/^#{1,6}\s+.+$/gm, '')  // Remove markdown headings
    .replace(/^\*\*[^*]+\*\*$/gm, '') // Remove bold text lines
    .trim();
}

/**
 * If file still has multiple default exports after splitting, keep only the first.
 */
function fixMultipleDefaultExports(code) {
  const matches = [...code.matchAll(/export default /g)];
  if (matches.length > 1) {
    return code.substring(0, matches[1].index).trim();
  }
  return code;
}
